import React, { useEffect, useState } from 'react'
import { Briefcase, Users, Building2, Clock, TrendingUp, CheckCircle2, ChevronRight, MessageSquare, AlertTriangle, X } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../store/authStore'
import { supabaseClient } from '../config/supabase'
import type { EmployerProfile, CompanyDepartmentPartnership } from '../types'

interface EmployerStats {
  activeJobs: number
  totalApplications: number
  pendingApplications: number
  reviewedShortlisted: number
  hiredAlumni: number
  profileComplete: boolean
}

const EmployerDashboard: React.FC = () => {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const [profile, setProfile] = useState<EmployerProfile | null>(null)
  const [partnerships, setPartnerships] = useState<CompanyDepartmentPartnership[]>([])
  const [stats, setStats] = useState<EmployerStats>({ activeJobs: 0, totalApplications: 0, pendingApplications: 0, reviewedShortlisted: 0, hiredAlumni: 0, profileComplete: false })
  const [loading, setLoading] = useState(true)
  const [showRejectionBanner, setShowRejectionBanner] = useState(false)
  const [rejectedDepts, setRejectedDepts] = useState<string[]>([])
  const [recentActivity, setRecentActivity] = useState<{ id: string; jobTitle: string; applicantName: string; appliedAt: string; status: string }[]>([])

  const getTimeAgo = (dateStr: string): string => {
    const date = new Date(dateStr)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)
    const diffWeeks = Math.floor(diffMs / 604800000)
    const diffMonths = Math.floor(diffMs / 2592000000)
    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays < 7) return `${diffDays}d ago`
    if (diffWeeks < 4) return `${diffWeeks}w ago`
    if (diffMonths < 12) return `${diffMonths}mo ago`
    return date.toLocaleDateString()
  }

  useEffect(() => {
    fetchData()
  }, [user])

  const fetchData = async () => {
    if (!user) return
    try {
      const { data: ep } = await supabaseClient
        .from('employer_profiles')
        .select('*, companies(*)')
        .eq('user_id', user.id)
        .single()

      if (ep) {
        setProfile(ep)

        // Fetch job stats
        const { count: activeJobCount } = await supabaseClient
          .from('jobs')
          .select('*', { count: 'exact', head: true })
          .eq('posted_by', user.id)
          .eq('status', 'active')
          .is('deleted_at', null)

        const { data: allJobs } = await supabaseClient
          .from('jobs')
          .select('id, title')
          .eq('posted_by', user.id)
          .is('deleted_at', null)

        let totalApps = 0
        let hiredCount = 0
        let pendingCount = 0
        let reviewedShortlistedCount = 0
        if (allJobs && allJobs.length > 0) {
          const jobIds = allJobs.map((j: any) => j.id)
          const jobTitleMap = Object.fromEntries(allJobs.map((j: any) => [j.id, j.title]))

          const [
            { count: appCount },
            { count: pendingApps },
            { count: reviewedApps },
            { count: hired },
            { data: recentAppsRaw }
          ] = await Promise.all([
            supabaseClient.from('job_applications').select('*', { count: 'exact', head: true }).in('job_id', jobIds).neq('status', 'cancelled'),
            supabaseClient.from('job_applications').select('*', { count: 'exact', head: true }).in('job_id', jobIds).eq('status', 'pending'),
            supabaseClient.from('job_applications').select('*', { count: 'exact', head: true }).in('job_id', jobIds).in('status', ['reviewed', 'shortlisted']),
            supabaseClient.from('job_applications').select('*', { count: 'exact', head: true }).in('job_id', jobIds).eq('status', 'hired'),
            supabaseClient.from('job_applications').select('id, alumni_id, job_id, applied_at, status').in('job_id', jobIds).neq('status', 'cancelled').order('applied_at', { ascending: false }).limit(5)
          ])

          totalApps = appCount || 0
          pendingCount = pendingApps || 0
          reviewedShortlistedCount = reviewedApps || 0
          hiredCount = hired || 0

          if (recentAppsRaw && recentAppsRaw.length > 0) {
            const alumniIds = [...new Set(recentAppsRaw.map((a: any) => a.alumni_id))]
            const { data: profilesData } = await supabaseClient
              .from('alumni_profiles')
              .select('user_id, first_name, last_name')
              .in('user_id', alumniIds)
            const nameMap = Object.fromEntries(
              (profilesData || []).map((p: any) => [p.user_id, `${p.first_name || ''} ${p.last_name || ''}`.trim() || 'Alumni'])
            )
            setRecentActivity(recentAppsRaw.map((a: any) => ({
              id: a.id,
              jobTitle: jobTitleMap[a.job_id] || 'Unknown Position',
              applicantName: nameMap[a.alumni_id] || 'Alumni',
              appliedAt: a.applied_at,
              status: a.status,
            })))
          }
        }

        setStats({
          activeJobs: activeJobCount || 0,
          totalApplications: totalApps,
          pendingApplications: pendingCount,
          reviewedShortlisted: reviewedShortlistedCount,
          hiredAlumni: hiredCount,
          profileComplete: !!(ep.contact_person && ep.position && ep.companies?.name && ep.companies?.description && ep.companies?.location),
        })

        // Full rejection — show banner once per session
        if (ep.approval_status === 'rejected') {
          const key = `partnerRejBannerSeen_${ep.user_id}`
          if (!sessionStorage.getItem(key)) {
            setShowRejectionBanner(true)
            sessionStorage.setItem(key, '1')
          }
        }

        // Fetch all partnerships to detect partial rejections
        if (ep.company_id && (ep.is_industry_partner || ep.approval_status === 'rejected')) {
          const { data: pData } = await supabaseClient
            .from('company_department_partnerships')
            .select('*, departments(name, abbreviation)')
            .eq('company_id', ep.company_id)

          const allPartners = pData || []
          setPartnerships(allPartners.filter((p: any) => p.status === 'approved'))

          // Partial rejection: is partner (some approved) but some depts were rejected
          if (ep.is_industry_partner && ep.approval_status === 'approved') {
            const rejected = allPartners.filter((p: any) => p.status === 'rejected')
            if (rejected.length > 0) {
              const key = `partnerPartialRejBannerSeen_${ep.user_id}`
              if (!sessionStorage.getItem(key)) {
                setRejectedDepts(rejected.map((p: any) => p.departments?.name || p.departments?.abbreviation || 'Unknown'))
                sessionStorage.setItem(key, '1')
              }
            }
          }
        }
      }
    } catch (err) {
      console.error('Error fetching employer dashboard data:', err)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
      </div>
    )
  }

  const company = (profile as any)?.companies
  const contactName = profile?.contact_person
    ? profile.contact_person
    : user?.email?.split('@')[0] || 'User'
  const hasApprovedIndustryPartner = !!profile?.is_industry_partner && profile?.approval_status === 'approved'

  return (
    <div className="space-y-0">

      {/* ── Welcome Banner ── */}
      <div className="mb-8 bg-blue-900 rounded-2xl p-6 sm:p-8 text-white relative overflow-hidden">
        {/* Decorative circles */}
        <div className="absolute -top-10 -right-10 w-52 h-52 bg-white/5 rounded-full pointer-events-none" />
        <div className="absolute -bottom-8 right-16 w-40 h-40 bg-yellow-400/10 rounded-full pointer-events-none" />
        <div className="absolute top-4 right-32 w-16 h-16 bg-white/5 rounded-full pointer-events-none" />

        <div className="relative">
          <div className="flex flex-wrap items-center gap-3 mb-1">
            <h1 className="text-2xl sm:text-3xl font-bold">
              Welcome back, {contactName}!
            </h1>
            {hasApprovedIndustryPartner && (
              <span className="px-3 py-1 rounded-full text-xs font-semibold bg-yellow-400 text-gray-900">
                Industry Partner
              </span>
            )}
          </div>
          <p className="text-blue-200 text-xs sm:text-sm">
            {company?.name
              ? `${company.name}${company.industry ? ` · ${company.industry}` : ''}`
              : 'Employer dashboard overview'}
          </p>

          {/* Bottom chips */}
          {!loading && (
            <div className="mt-5 flex flex-wrap gap-3">
              {partnerships.length > 0 && (
                <span className="bg-yellow-400 text-gray-900 rounded-full px-3 py-1.5 text-sm font-bold flex items-center gap-1.5">
                  <Building2 className="w-3.5 h-3.5" />
                  {partnerships.length} approved {partnerships.length === 1 ? 'partnership' : 'partnerships'}
                </span>
              )}
              <span className="bg-white/10 rounded-full px-3 py-1.5 text-sm font-medium text-white flex items-center gap-1.5">
                <Briefcase className="w-3.5 h-3.5 text-yellow-300" />
                {stats.activeJobs} active job {stats.activeJobs === 1 ? 'post' : 'posts'}
              </span>
              <span className="bg-white/10 rounded-full px-3 py-1.5 text-sm font-medium text-white flex items-center gap-1.5">
                <TrendingUp className="w-3.5 h-3.5 text-yellow-300" />
                {stats.totalApplications} total applications
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Full rejection banner — shown once per login session */}
      {showRejectionBanner && (
        <div className="mb-8 rounded-xl border border-red-200 bg-red-50 p-4 sm:p-5">
          <div className="flex items-start gap-3">
            <div className="rounded-full bg-red-100 p-2 mt-0.5 flex-shrink-0">
              <AlertTriangle className="w-4 h-4 text-red-700" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-red-800">Your industry partnership request was rejected.</p>
              <p className="mt-1 text-xs sm:text-sm text-red-700">
                Your account remains active as a regular employer. You can review the reason and submit a new request after the cooldown period.
              </p>
              <button
                type="button"
                onClick={() => navigate('/employer/profile')}
                className="mt-3 inline-flex items-center text-xs font-semibold text-red-800 hover:underline"
              >
                Review rejection details and reapply options
              </button>
            </div>
            <button
              type="button"
              onClick={() => setShowRejectionBanner(false)}
              className="text-red-400 hover:text-red-600 p-1 rounded flex-shrink-0"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Partial rejection banner — shown once per login session */}
      {rejectedDepts.length > 0 && (
        <div className="mb-8 rounded-xl border border-amber-200 bg-amber-50 p-4 sm:p-5">
          <div className="flex items-start gap-3">
            <div className="rounded-full bg-amber-100 p-2 mt-0.5 flex-shrink-0">
              <AlertTriangle className="w-4 h-4 text-amber-700" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-amber-800">Some partnership requests were not approved.</p>
              <p className="mt-1 text-xs sm:text-sm text-amber-700">
                Your account is active as an industry partner for the approved departments, but the following{' '}
                {rejectedDepts.length === 1 ? 'department' : 'departments'} rejected your request:{' '}
                <span className="font-semibold">{rejectedDepts.join(', ')}</span>.
                You may reapply after the cooldown period.
              </p>
              <button
                type="button"
                onClick={() => navigate('/employer/profile')}
                className="mt-3 inline-flex items-center text-xs font-semibold text-amber-800 hover:underline"
              >
                Review rejection details and reapply options
              </button>
            </div>
            <button
              type="button"
              onClick={() => setRejectedDepts([])}
              className="text-amber-400 hover:text-amber-600 p-1 rounded flex-shrink-0"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* ── Overview KPI Cards ── */}
      <div className="mb-12">
        <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-6">Overview</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 sm:gap-6">
          {/* Active Job Posts */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
            <div className="bg-blue-900 px-5 py-3 flex items-center gap-2">
              <Briefcase className="w-4 h-4 text-white/80" />
              <span className="text-sm font-medium text-white/90">Active Job Posts</span>
            </div>
            <div className="px-5 py-4">
              <p className="text-3xl font-bold text-gray-900">{loading ? '—' : stats.activeJobs}</p>
              <p className="text-xs text-gray-400 mt-1">Currently listed openings</p>
            </div>
          </div>

          {/* Total Applications */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
            <div className="bg-yellow-400 px-5 py-3 flex items-center gap-2">
              <Users className="w-4 h-4 text-gray-800" />
              <span className="text-sm font-medium text-gray-800">Total Applications</span>
            </div>
            <div className="px-5 py-4">
              <p className="text-3xl font-bold text-gray-900">{loading ? '—' : stats.totalApplications}</p>
              <p className="text-xs text-gray-400 mt-1">Across all job posts</p>
            </div>
          </div>

          {/* Hired Alumni */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
            <div className="bg-blue-900 px-5 py-3 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-white/80" />
              <span className="text-sm font-medium text-white/90">Hired Alumni</span>
            </div>
            <div className="px-5 py-4">
              <p className="text-3xl font-bold text-gray-900">{loading ? '—' : stats.hiredAlumni}</p>
              <p className="text-xs text-gray-400 mt-1">UCLM alumni onboarded</p>
            </div>
          </div>

          {/* Partnerships */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
            <div className="bg-yellow-400 px-5 py-3 flex items-center gap-2">
              <Building2 className="w-4 h-4 text-gray-800" />
              <span className="text-sm font-medium text-gray-800">Partnerships</span>
            </div>
            <div className="px-5 py-4">
              <p className="text-3xl font-bold text-gray-900">{loading ? '—' : partnerships.length}</p>
              {partnerships.length > 0 ? (
                <p className="text-xs text-blue-600 mt-1 font-medium">
                  {partnerships.map((p: any) => p.departments?.abbreviation || p.departments?.name).join(', ')}
                </p>
              ) : (
                <p className="text-xs text-gray-400 mt-1">No active partnerships yet</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Recruitment Pipeline ── */}
      <div className="pt-5 mb-12">
        <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-5">Recruitment Pipeline</h2>
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-start gap-3 sm:gap-0">

            {/* Stage 1 — Active Jobs */}
            <div className="flex-1 flex flex-col items-center text-center px-4 py-5 rounded-xl bg-blue-50 border border-blue-100">
              <div className="w-10 h-10 rounded-full bg-blue-900 flex items-center justify-center mb-3">
                <Briefcase className="w-5 h-5 text-white" />
              </div>
              <p className="text-2xl font-bold text-gray-900">{stats.activeJobs}</p>
              <p className="text-xs font-semibold text-gray-700 mt-1">Active Job Posts</p>
              <p className="text-[11px] text-gray-400 mt-1 leading-tight">Open listings accepting applications</p>
            </div>

            <div className="hidden sm:flex items-center justify-center px-1 pt-5">
              <ChevronRight className="w-5 h-5 text-gray-300" />
            </div>
            <div className="sm:hidden flex justify-center"><div className="w-0.5 h-4 bg-gray-200" /></div>

            {/* Stage 2 — Pending Review */}
            <div className="flex-1 flex flex-col items-center text-center px-4 py-5 rounded-xl bg-yellow-50 border border-yellow-100">
              <div className="w-10 h-10 rounded-full bg-yellow-400 flex items-center justify-center mb-3">
                <Users className="w-5 h-5 text-gray-800" />
              </div>
              <p className="text-2xl font-bold text-gray-900">{stats.pendingApplications}</p>
              <p className="text-xs font-semibold text-gray-700 mt-1">Pending Review</p>
              <p className="text-[11px] text-gray-400 mt-1 leading-tight">Awaiting your action</p>
            </div>

            <div className="hidden sm:flex items-center justify-center px-1 pt-5">
              <ChevronRight className="w-5 h-5 text-gray-300" />
            </div>
            <div className="sm:hidden flex justify-center"><div className="w-0.5 h-4 bg-gray-200" /></div>

            {/* Stage 3 — In Progress */}
            <div className="flex-1 flex flex-col items-center text-center px-4 py-5 rounded-xl bg-blue-50 border border-blue-100">
              <div className="w-10 h-10 rounded-full bg-blue-900 flex items-center justify-center mb-3">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <p className="text-2xl font-bold text-gray-900">{stats.reviewedShortlisted}</p>
              <p className="text-xs font-semibold text-gray-700 mt-1">In Progress</p>
              <p className="text-[11px] text-gray-400 mt-1 leading-tight">Under review or shortlisted</p>
            </div>

            <div className="hidden sm:flex items-center justify-center px-1 pt-5">
              <ChevronRight className="w-5 h-5 text-gray-300" />
            </div>
            <div className="sm:hidden flex justify-center"><div className="w-0.5 h-4 bg-gray-200" /></div>

            {/* Stage 4 — Hired */}
            <div className="flex-1 flex flex-col items-center text-center px-4 py-5 rounded-xl bg-yellow-50 border border-yellow-100">
              <div className="w-10 h-10 rounded-full bg-yellow-400 flex items-center justify-center mb-3">
                <TrendingUp className="w-5 h-5 text-gray-800" />
              </div>
              <p className="text-2xl font-bold text-gray-900">{stats.hiredAlumni}</p>
              <p className="text-xs font-semibold text-gray-700 mt-1">Hired Alumni</p>
              <p className="text-[11px] text-gray-400 mt-1 leading-tight">UCLM alumni successfully onboarded</p>
            </div>

          </div>
        </div>
      </div>

      {/* ── Attention Needed ── */}
      <div className="pt-5 mb-12">
        <div className="flex items-center gap-3 mb-5">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Attention Needed</h2>
          {(() => {
            const count = (!stats.profileComplete ? 1 : 0) + (stats.pendingApplications > 0 ? 1 : 0)
            return count > 0 ? (
              <span className="px-2.5 py-0.5 bg-yellow-100 text-yellow-700 text-xs font-semibold rounded-full">
                {count} item{count !== 1 ? 's' : ''}
              </span>
            ) : null
          })()}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">

          {/* Company Profile */}
          <button onClick={() => navigate('/employer/profile')} className="text-left group">
            <div className={`rounded-xl p-5 shadow-sm hover:shadow-md transition-all border-l-4 h-full ${
              stats.profileComplete ? 'bg-white border-green-400' : 'bg-yellow-50 border-yellow-400'
            }`}>
              <div className="flex items-center justify-between mb-4">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  stats.profileComplete ? 'bg-green-100' : 'bg-yellow-400'
                }`}>
                  <Building2 className={`w-5 h-5 ${stats.profileComplete ? 'text-green-600' : 'text-gray-800'}`} />
                </div>
                {stats.profileComplete
                  ? <CheckCircle2 className="w-5 h-5 text-green-400" />
                  : <span className="px-2.5 py-1 bg-yellow-400 text-gray-800 rounded-full text-xs font-bold">1</span>
                }
              </div>
              <p className="font-semibold text-gray-900 text-sm">Company Profile</p>
              <p className="text-xs text-gray-500 mt-1">
                {stats.profileComplete ? 'Your profile is up to date.' : (() => {
                  const missing = []
                  const ep2 = profile as any
                  if (!ep2?.contact_person) missing.push('contact name')
                  if (!ep2?.position) missing.push('your position')
                  if (!ep2?.companies?.description) missing.push('company description')
                  if (!ep2?.companies?.location) missing.push('company location')
                  return `Missing: ${missing.join(', ')}.`
                })()}
              </p>
              <p className="text-xs text-blue-700 mt-3 font-medium group-hover:underline">Go to Profile →</p>
            </div>
          </button>

          {/* New Applications */}
          <button onClick={() => navigate('/employer/applications')} className="text-left group">
            <div className={`rounded-xl p-5 shadow-sm hover:shadow-md transition-all border-l-4 h-full ${
              stats.pendingApplications > 0 ? 'bg-yellow-50 border-yellow-400' : 'bg-white border-green-400'
            }`}>
              <div className="flex items-center justify-between mb-4">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  stats.pendingApplications > 0 ? 'bg-yellow-400' : 'bg-green-100'
                }`}>
                  <Users className={`w-5 h-5 ${stats.pendingApplications > 0 ? 'text-gray-800' : 'text-green-600'}`} />
                </div>
                {stats.pendingApplications > 0
                  ? <span className="px-2.5 py-1 bg-yellow-400 text-gray-800 rounded-full text-xs font-bold">{stats.pendingApplications}</span>
                  : <CheckCircle2 className="w-5 h-5 text-green-400" />
                }
              </div>
              <p className="font-semibold text-gray-900 text-sm">New Applications</p>
              <p className="text-xs text-gray-500 mt-1">
                {stats.pendingApplications > 0
                  ? `${stats.pendingApplications} application${stats.pendingApplications !== 1 ? 's' : ''} awaiting review.`
                  : 'No pending applications.'}
              </p>
              <p className="text-xs text-blue-700 mt-3 font-medium group-hover:underline">Manage Applications →</p>
            </div>
          </button>

          {/* Alumni Feedback */}
          {stats.hiredAlumni > 0 ? (
            <button onClick={() => navigate('/employer/feedback')} className="text-left group">
              <div className="rounded-xl p-5 shadow-sm hover:shadow-md transition-all border-l-4 border-blue-200 bg-white h-full">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-blue-100">
                    <MessageSquare className="w-5 h-5 text-blue-700" />
                  </div>
                  <CheckCircle2 className="w-5 h-5 text-green-400" />
                </div>
                <p className="font-semibold text-gray-900 text-sm">Alumni Feedback</p>
                <p className="text-xs text-gray-500 mt-1">Request feedback from hired alumni about their experience working with your company.</p>
                <p className="text-xs text-blue-700 mt-3 font-medium group-hover:underline">View Feedback →</p>
              </div>
            </button>
          ) : (
            <div className="opacity-50 cursor-not-allowed select-none">
              <div className="rounded-xl p-5 border-l-4 border-gray-200 bg-white h-full">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-gray-100">
                    <MessageSquare className="w-5 h-5 text-gray-400" />
                  </div>
                </div>
                <p className="font-semibold text-gray-400 text-sm">Alumni Feedback</p>
                <p className="text-xs text-gray-400 mt-1">No hired alumni yet. Hire alumni to request feedback.</p>
              </div>
            </div>
          )}

        </div>
      </div>

      {/* ── Recent Activity ── */}
      <div className="pt-5 mb-12">
        <div className="flex items-center gap-3 mb-5">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Recent Activity</h2>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
          {recentActivity.length > 0 ? recentActivity.map(act => {
            const statusConfig: Record<string, { label: string; color: string }> = {
              pending:     { label: 'New Application', color: 'text-blue-600' },
              reviewed:    { label: 'Under Review',    color: 'text-indigo-600' },
              shortlisted: { label: 'Shortlisted',     color: 'text-green-600' },
              hired:       { label: 'Hired',           color: 'text-emerald-600' },
              rejected:    { label: 'Rejected',        color: 'text-red-500' },
            }
            const sc = statusConfig[act.status] ?? { label: act.status, color: 'text-gray-500' }
            return (
              <button
                key={act.id}
                onClick={() => navigate('/employer/applications')}
                className="w-full text-left px-5 py-3.5 flex items-center justify-between hover:bg-gray-50 transition"
              >
                <div className="min-w-0">
                  <p className="text-sm text-gray-800 truncate">
                    <span className="font-medium">{act.applicantName}</span>{' applied for '}
                    <span className="font-medium">{act.jobTitle}</span>
                  </p>
                  <p className={`text-xs mt-0.5 font-medium ${sc.color}`}>{sc.label}</p>
                </div>
                <p className="text-xs text-gray-400 ml-4 shrink-0">{getTimeAgo(act.appliedAt)}</p>
              </button>
            )
          }) : (
            <div className="px-5 py-10 flex flex-col items-center text-center text-gray-400">
              <Clock className="w-8 h-8 mb-2 text-gray-300" />
              <p className="text-sm font-medium text-gray-500">No recent activity yet</p>
              <p className="text-xs text-gray-400 mt-1">Applications will appear here once alumni start applying.</p>
            </div>
          )}
        </div>
      </div>

    </div>
  )
}

export default EmployerDashboard
